/**
 * НАЗНАЧЕНИЕ: Массовое удаление слайдов, выбранных чекбоксами в таблице
 * ОПИСАНИЕ: Пункт левого меню «Удаление». Работает с тем же списком
 * выделенных слайдов (store.slides.selected), что и «Экспорт»
 * БЕЗОПАСНОСТЬ: Удаление необратимо (вместе со слайдом удаляются аннотации
 * и файлы на диске), поэтому запрашивается подтверждение
 * РЕЗУЛЬТАТ: Строки убираются из таблицы, список перезапрашивается с сервера
 */
import React, {useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

import {deleteSlides} from "../../../shared/api/slides";
import {
    fetchSlidesByPage,
    removeAllSelected,
    removeSlides,
} from "../../../shared/model/redux/slides-reducer";

export const Delete = () => {

    const dispatch = useDispatch();
    const selected = useSelector((store) => store.slides.selected);
    const requestParams = useSelector((store) => store.slides.requestParams);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [result, setResult] = useState(null);

    const deleteSlidesHandler = async () => {
        const slideIds = selected.map((slide) => slide.id);

        setConfirmOpen(false);
        setIsDeleting(true);
        setResult(null);

        try {
            const data = await deleteSlides(slideIds);
            const deletedIds = (data.deleted || []).map((slide) => slide.id);

            // Сразу убираем удаленные строки, затем обновляем список и пагинацию
            dispatch(removeSlides(deletedIds));
            dispatch(removeAllSelected());
            dispatch(fetchSlidesByPage(requestParams));

            setResult({
                deletedCount: deletedIds.length,
                notFound: data.not_found || [],
                errors: data.errors || [],
                filesFailed: data.files_failed || [],
            });
        } catch (error) {
            console.error("Ошибка удаления слайдов:", error);
            setResult({
                deletedCount: 0,
                notFound: [],
                errors: [],
                message:
                    error.response?.data?.error ||
                    "Не удалось удалить слайды. Повторите попытку позднее",
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const renderResult = () => {
        if (!result) return null;

        if (result.message) {
            return <p style={{color: "#d32f2f"}}>{result.message}</p>;
        }

        return (
            <>
                {result.deletedCount > 0 && (
                    <p style={{color: "#388e3c"}}>
                        Удалено слайдов: {result.deletedCount}
                    </p>
                )}
                {result.notFound.length > 0 && (
                    <p style={{color: "#f57c00"}}>
                        Не найдено или недоступно: {result.notFound.length}
                    </p>
                )}
                {result.errors.length > 0 && (
                    <p style={{color: "#d32f2f"}}>
                        Не удалось удалить: {result.errors
                            .map((item) => item.name)
                            .join(", ")}
                    </p>
                )}
                {result.filesFailed?.length > 0 && (
                    <p style={{color: "#d32f2f"}}>
                        Записи удалены, но файлы остались на сервере: {result.filesFailed
                            .map((item) => item.name)
                            .join(", ")}
                    </p>
                )}
            </>
        );
    };

    if (selected.length === 0) {
        return (
            <div onClick={(e) => e.stopPropagation()}>
                <p>Список для удаления - пуст</p>
                {renderResult()}
            </div>
        );
    }

    return (
        // Пункт меню сворачивается по клику, поэтому клики внутри панели
        // не пропускаем наверх
        <div onClick={(e) => e.stopPropagation()}>
            <ul className="menu-selected-list">
                {selected.map((item, index) => (
                    <li key={item.id + index}>{item.name}</li>
                ))}
            </ul>
            <Button
                color="error"
                variant="contained"
                size="medium"
                style={{marginTop: '10px'}}
                disabled={isDeleting}
                onClick={() => setConfirmOpen(true)}
            >
                {isDeleting ? "Удаление..." : "Удалить"}
            </Button>
            {renderResult()}

            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">Подтвердите удаление</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Будет удалено слайдов: {selected.length}. Вместе со слайдами
                        удаляются их аннотации и файлы. Действие необратимо.
                    </DialogContentText>
                    <ul>
                        {selected.map((item, index) => (
                            <li key={item.id + index}>{item.name}</li>
                        ))}
                    </ul>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} color="primary">
                        Отменить
                    </Button>
                    <Button onClick={deleteSlidesHandler} color="error" autoFocus>
                        Удалить
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};
